import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";
import StoriesViewer from "./StoriesViewer";
import type { Tables } from "@/integrations/supabase/types";

interface InteractiveCardProps {
  templateUrl: string;
  title: string;
  messages: Tables<"messages">[];
  contributions: Tables<"contributions">[];
  envelopeTint?: string;
  musicId?: string | null;
}

type CardState = "tucked" | "resting" | "animating";

// Positions as bottom % of the envelope wrapper
const POS_TUCKED = "12%";   // inside envelope
const POS_ABOVE = "68%";    // above envelope (reduced so card doesn't go behind header)
const POS_RESTING = "15%";  // resting with bottom edge near envelope bottom

const EASE_UP = [0.22, 1, 0.36, 1] as const;
const EASE_DOWN = [0.25, 0.1, 0.25, 1] as const;

const InteractiveCard = ({ templateUrl, title, messages, contributions, envelopeTint, musicId }: InteractiveCardProps) => {
  const [state, setState] = useState<CardState>("tucked");
  const [hasAnimatedIntro, setHasAnimatedIntro] = useState(false);
  const [showStories, setShowStories] = useState(false);
  
  const animating = useRef(false);
  const controls = useAnimationControls();

  const envelopeFilterStyle = envelopeTint || undefined;

  const handleToggle = async () => {
    if (!hasAnimatedIntro || animating.current) return;
    animating.current = true;
    controls.stop(); // Stop idle bounce
    setState("animating");

    if (state === "tucked") {
      // Opening: UP above envelope, then DOWN to rest on top
      await controls.start({ bottom: POS_ABOVE, transition: { duration: 0.4, ease: EASE_UP } });
      await controls.start({ bottom: POS_RESTING, transition: { duration: 0.45, ease: EASE_DOWN } });
      setState("resting");
    } else {
      // Closing: straight up then directly into envelope (no mid-stop)
      await controls.start({ bottom: POS_ABOVE, transition: { duration: 0.35, ease: EASE_UP } });
      await controls.start({ bottom: POS_TUCKED, transition: { duration: 0.5, ease: EASE_DOWN } });
      setState("tucked");
      // Restart idle bounce
      controls.start({
        bottom: [POS_TUCKED, "15%", POS_TUCKED, "14%", POS_TUCKED],
        transition: { duration: 1.6, ease: "easeInOut", repeat: Infinity, repeatDelay: 3 },
      });
    }
    animating.current = false;
  };

  

  return (
    <>
      {/* Fixed-size self-contained wrapper — never changes height */}
      <div
        className="relative mx-auto select-none cursor-pointer overflow-hidden"
        style={{ width: "100%", maxWidth: 520, aspectRatio: "500/720" }}
        onClick={handleToggle}
      >
        {/* Envelope + card container pinned to bottom */}
        <div className="absolute bottom-0 left-0 right-0" style={{ aspectRatio: "500/720" }}>
          {/* Card element — slides up/down, absolutely positioned */}
          <motion.div
            className={`absolute left-1/2 ${state === "tucked" ? "z-[1]" : "z-[10]"}`}
            style={{
              width: "78%",
              x: "-50%",
              aspectRatio: "360/508",
            }}
            initial={{ bottom: POS_TUCKED, rotate: 15 }}
            animate={hasAnimatedIntro ? controls : { bottom: POS_TUCKED, rotate: 0 }}
            transition={
              !hasAnimatedIntro
                ? { delay: 0.9, duration: 1.2, ease: EASE_UP }
                : undefined
            }
            onAnimationComplete={() => {
              if (!hasAnimatedIntro) {
                setHasAnimatedIntro(true);
                // Subtle bounce hint to indicate interactivity
                controls.start({
                  bottom: [POS_TUCKED, "15%", POS_TUCKED, "14%", POS_TUCKED],
                  transition: { duration: 1.6, ease: "easeInOut", repeat: Infinity, repeatDelay: 3 },
                });
              }
            }}
          >
            <motion.div
              className="w-full h-full overflow-hidden bg-white rounded-sm"
              initial={{ boxShadow: "0 1px 3px 0 rgba(0,0,0,0.08)" }}
              animate={{
                boxShadow: state === "resting"
                  ? "0 4px 16px -2px rgba(0,0,0,0.12), 0 1px 4px -1px rgba(0,0,0,0.06)"
                  : "0 2px 8px -1px rgba(0,0,0,0.08), 0 1px 3px 0 rgba(0,0,0,0.04)",
              }}
              transition={{ duration: 0.4, ease: EASE_DOWN }}
            >
              {templateUrl && (
                <img src={templateUrl} alt="Card" className="w-full h-full object-cover" />
              )}
              {title && !templateUrl && (
                <div className="flex items-center justify-center h-full p-6">
                  <h3 className="text-2xl font-bold text-center text-foreground">{title}</h3>
                </div>
              )}
            </motion.div>
          </motion.div>

          {/* Envelope back */}
          <img
            src="/images/envelope-back.png"
            alt=""
            className="absolute left-0 right-0 bottom-0 w-full pointer-events-none"
            style={{ zIndex: 0, filter: envelopeFilterStyle }}
          />

          {/* Envelope front */}
          <img
            src="/images/envelope-front.png"
            alt=""
            className="absolute left-0 right-0 bottom-0 w-full pointer-events-none"
            style={{ zIndex: 8, filter: envelopeFilterStyle }}
          />

          {/* Tap hint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: hasAnimatedIntro ? 0 : 2.2 }}
            className="absolute inset-0 flex items-end justify-center pb-[2%] z-10"
          >
            <span className="text-[10px] text-muted-foreground/60 font-medium tracking-wide animate-pulse">
              {state === "tucked" ? "Tap to open" : state === "resting" ? "Tap to close" : ""}
            </span>
          </motion.div>
        </div>
      </div>


      {/* Stories viewer */}
      {createPortal(
        <AnimatePresence>
          {showStories && (
            <StoriesViewer
              templateUrl={templateUrl}
              title={title}
              messages={messages}
              contributions={contributions}
              onClose={() => setShowStories(false)}
              musicId={musicId}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default InteractiveCard;
